import { uniqBy } from "lodash-es";
import { defineStore } from "pinia";

import { getStickies, resolveObjectives } from "@/backend/read/BackendReader";
import { addSticky } from "@/backend/read/changeSticky";
import { captureMessage } from "@/error/sentry";
import type { Id } from "@/model/baseTypes";
import type { Board, BoardId, BoardWithObjectives } from "@/model/board";
import { isArtBoard, isSolutionBoard, isTeamBoard } from "@/model/board";
import type { Card } from "@/model/card";
import type { LinkableCardTree } from "@/model/link";
import type {
  LinkedObjective,
  Objective,
  ObjectiveType,
  ObjectiveUpdate,
  TreeSticky,
} from "@/model/objective";
import type { Team } from "@/model/session";
import { isDependency, isNote } from "@/model/stickyType";
import { objectId } from "@/utils/objectId";

import { useBoardStore } from "./board";
import { useBoardsStore } from "./boards";
import { isCardFromTeam, useCardStore } from "./card";
import { getLinkTargetId } from "./helpers/link";
import { sortCardsHierarchically } from "./helpers/linkableItemsSort";
import { useLinkStore } from "./link";

export const useObjectiveStore = defineStore("objective", () => {
  function objectiveById(
    objectiveId: Objective["id"],
    { teamId, boardId }: { teamId?: Team["id"]; boardId?: Board["id"] } = {},
  ): Objective | undefined {
    const board = boardId
      ? useBoardsStore().boardById(boardId)
      : useBoardsStore().boardByType("team", { teamId });
    const objectives = [
      ...(("objectives" in board && board.objectives) || []),
      ...(("stretchObjectives" in board && board.stretchObjectives) || []),
    ];

    return objectives.find((objective) => objective.id === objectiveId);
  }

  function objectiveTypeById(
    objectiveId: Objective["id"],
    { teamId, boardId }: { teamId?: Team["id"]; boardId?: Board["id"] } = {},
  ): ObjectiveType | undefined {
    const objective = objectiveById(objectiveId, { teamId, boardId });
    if (!objective) return;
    const board = useBoardsStore().boardByType("team", { teamId });
    const isInObjectives = board.objectives.some((objective) => objective.id === objectiveId);
    return isInObjectives ? "committed" : "uncommitted";
  }

  function childrenLinkedToCard(card: Card) {
    // Only show linked items that are 1 'level' below in the hierarchy,
    if (
      isNote(card) || // notes are never above items in hierarchy
      (isTeamBoard(card.type.origin) && !isDependency(card)) // team board workitems/risks are at the bottom of the hierarchy
    ) {
      return [];
    }

    const id = card.groupId ?? card.id;
    return sortCardsHierarchically(
      useLinkStore()
        .linksByCard(id)
        .map((link) => useCardStore().cards[getLinkTargetId(card, link)])
        .filter((linkedCard) => shouldShowSecondaryLink(card, linkedCard)),
    );

    function shouldShowSecondaryLink(card: Card, secondaryCard?: Card) {
      if (!secondaryCard) return false;

      if (isDependency(card)) {
        return isCardFromTeam(secondaryCard, card.dependTeam?.id);
      }

      const board = card.type.origin;

      if (isSolutionBoard(board)) {
        // For solution-level items, show art/team level linked-items
        return !isSolutionBoard(secondaryCard.type.origin);
      }

      if (isArtBoard(board)) {
        // For art-level items, show team level linked-items
        return isTeamBoard(secondaryCard.type.origin);
      }

      return false;
    }
  }

  // Mirror of childrenLinkedToCard: keep linked cards one level above in the
  // hierarchy. Dependencies are lateral and therefore excluded.
  function parentsLinkedToCard(card: Card) {
    if (isNote(card) || isSolutionBoard(card.type.origin)) {
      return [];
    }

    const id = card.groupId ?? card.id;
    const linkStore = useLinkStore();
    const missingCards = new Array<string>();
    const parents = sortCardsHierarchically(
      linkStore
        .linksByCard(id)
        .map((link) => {
          const targetId = getLinkTargetId(card, link);
          const linkedCard = linkStore.cardByLink(targetId);
          if (!linkedCard) missingCards.push(targetId);
          return linkedCard;
        })
        .filter((linkedCard): linkedCard is Card => shouldShowPrimaryLink(card, linkedCard)),
    );

    if (missingCards.length) {
      void getStickies(missingCards).then((stickies) => stickies.forEach(addSticky));
    }

    return parents;

    function shouldShowPrimaryLink(card: Card, primaryCard?: Card) {
      if (!primaryCard) return false;
      if (isDependency(card) || isDependency(primaryCard)) return false;

      const board = card.type.origin;
      if (isTeamBoard(board)) {
        return isArtBoard(primaryCard.type.origin) || isSolutionBoard(primaryCard.type.origin);
      }
      if (isArtBoard(board)) {
        return isSolutionBoard(primaryCard.type.origin);
      }
      return false;
    }
  }

  function linkedCardTree(board: BoardWithObjectives, objective: Objective): LinkableCardTree {
    if (objective.cardTree) {
      if (!board.objectivesResolved) {
        void resolveObjectives(board);
        board.objectivesResolved = true;
      }
      const cardStore = useCardStore();
      const resolvedCards = objective.cardTree.cards
        .map((card) => ({ ...cardStore.cards[card.id], ...card }))
        .filter((c) => c.type);
      return {
        ...objective.cardTree,
        cards: sortCardsHierarchically(resolvedCards),
      };
    }
    const indirectIds = new Set<string>();
    const missingCards = new Array<string>();
    const cards = sortCardsHierarchically(
      objective.cards.flatMap((ref) => {
        if (!ref.isOrigin) {
          return [];
        }
        const card = useCardStore().cards[ref.id];
        if (!card) {
          missingCards.push(ref.id);
          return [];
        }
        // Collect missing children so they can be fetched on demand
        const cardLinkId = card.groupId ?? card.id;
        for (const link of useLinkStore().linksByCard(cardLinkId)) {
          const targetId = getLinkTargetId(card, link);
          if (!useCardStore().cards[targetId]) {
            missingCards.push(targetId);
          }
        }
        const children = childrenLinkedToCard(card).map((card) => ({
          id: card.id,
          children: [],
        }));
        children.forEach((child) => indirectIds.add(child.id));
        return { ...card, statusClass: card.status?.statusClass, children, linked: true };
      }),
    );
    if (missingCards.length) {
      void getStickies(missingCards).then((stickies) => stickies.forEach(addSticky));
    }
    cards.forEach((card) => indirectIds.delete(card.id));

    return { cards, statusDistribution: new Map(), indirectLinkedIds: [...indirectIds] };
  }

  function linkedCardsFlat(board: BoardWithObjectives, objective: Objective): TreeSticky[] {
    const tree = linkedCardTree(board, objective);
    const allCards = tree.cards.flatMap((card) => [card, ...card.children]);
    return uniqBy(allCards, "id");
  }

  function add(e: Partial<Objective> & BoardId, isCommitted = true) {
    const board = useBoardsStore().boards[e.boardId] as BoardWithObjectives;
    const obj = {
      id: e.id || objectId(),
      text: e.text || "",
      bv: 0,
      av: null,
      description: e.description || "",
      rag: e.rag || "green",
      cards: e.cards ?? [],
      linkedObjectives: e.linkedObjectives ?? [],
      cardTree: e.cardTree,
    };
    (isCommitted ? board.objectives : board.stretchObjectives).unshift(obj);
    return obj;
  }

  function remove(item: Id & BoardId) {
    const boardId = item.boardId || useBoardStore().boardId();
    const board = useBoardsStore().boards[boardId] as BoardWithObjectives;
    const objectiveIndex = board.objectives.findIndex((o) => o.id === item.id);

    if (objectiveIndex > -1) {
      board.objectives.splice(objectiveIndex, 1);
    }

    const stretchObjectiveIndex = board.stretchObjectives.findIndex((o) => o.id === item.id);
    if (stretchObjectiveIndex > -1) {
      board.stretchObjectives.splice(stretchObjectiveIndex, 1);
    }

    // Drop the reverse objective-to-objective links so that the link chip
    // counts on the objectives that pointed at this one update immediately,
    // without waiting for a reload.
    removeLinkedObjectiveFromAll({ id: item.id, boardId });
  }

  function move(e: Id & BoardId & { stretch: boolean; rank: number }) {
    const boardId = e.boardId || useBoardStore().boardId();
    const board = useBoardsStore().boards[boardId] as BoardWithObjectives;
    const { objective, objectives, pos } = findObjective(board, e.id);
    if (!objective) {
      captureMessage("Move of unknown objective", {
        info: {
          data: e,
          boardType: board.type,
          objectives: board.objectives.length,
          stretch: board.stretchObjectives.length,
        },
      });
      return;
    }
    objectives.splice(pos, 1);
    const target = e.stretch ? board.stretchObjectives : board.objectives;
    target.splice(e.rank, 0, objective);
  }

  function update(e: Id & BoardId & Partial<ObjectiveUpdate>) {
    const board = useBoardsStore().boards[e.boardId] as BoardWithObjectives;
    const { objective } = findObjective(board, e.id);
    if (!objective) {
      if (e.text === "") {
        add(e);
      } else {
        captureMessage("Update of unknown objective", {
          info: {
            data: e,
            boardType: board.type,
            objectives: board.objectives.length,
            stretch: board.stretchObjectives.length,
          },
        });
      }
      return;
    }

    objective.text = e.text ?? objective.text;
    objective.description = e.description ?? objective.description;
    objective.bv = e.bv ?? objective.bv;
    objective.av = e.av ?? objective.av;
    objective.cards = e.cards ?? objective.cards;
    objective.linkedObjectives = e.linkedObjectives ?? objective.linkedObjectives;
    objective.cardTree = e.cardTree ?? objective.cardTree;
    objective.rag = e.rag ?? objective.rag;
  }

  function addLinkedObjective(
    boardId: Board["id"],
    objectiveId: Objective["id"],
    linked: LinkedObjective,
  ) {
    const objective = objectiveById(objectiveId, { boardId });
    if (!objective) return;
    const alreadyLinked = objective.linkedObjectives.some((l) => sameLinkedObjective(l, linked));
    if (!alreadyLinked) {
      objective.linkedObjectives.push(linked);
    }
  }

  function removeLinkedObjective(
    boardId: Board["id"],
    objectiveId: Objective["id"],
    linked: LinkedObjective,
  ) {
    const objective = objectiveById(objectiveId, { boardId });
    if (!objective) return;
    objective.linkedObjectives = objective.linkedObjectives.filter(
      (l) => !sameLinkedObjective(l, linked),
    );
  }

  return {
    objectiveById,
    objectiveTypeById,
    childrenLinkedToCard,
    parentsLinkedToCard,
    linkedCardTree,
    linkedCardsFlat,

    add,
    remove,
    move,
    update,
    addLinkedObjective,
    removeLinkedObjective,
  };
});

function sameLinkedObjective(a: LinkedObjective, b: LinkedObjective): boolean {
  return a.id === b.id && a.boardId === b.boardId;
}

function findObjective(
  board: BoardWithObjectives,
  id: string,
): { objective: Objective; objectives: Objective[]; pos: number } {
  let objectives = board.objectives;
  let pos = objectives.findIndex((o) => o.id === id);
  if (pos < 0) {
    objectives = board.stretchObjectives;
    pos = objectives.findIndex((o) => o.id === id);
  }
  return { objective: objectives[pos], objectives, pos };
}

function removeLinkedObjectiveFromAll(linked: LinkedObjective) {
  useBoardsStore().boardsWithObjectives.forEach((board) =>
    [...board.objectives, ...board.stretchObjectives].forEach((objective) => {
      objective.linkedObjectives = objective.linkedObjectives.filter(
        (l) => l.id !== linked.id || l.boardId !== linked.boardId,
      );
    }),
  );
}
